import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { PaginationControls } from '../../components/PaginationControls';
import { chatHistoryApiService } from '../../services/chatHistoryApi';
import { ChatInteraction } from '../../services/chatHistoryService';

export const AdminChatHistory: React.FC = () => {
  const [interactions, setInteractions] = useState<ChatInteraction[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    source: '' as ChatInteraction['source'] | '',
    dateFrom: '',
    dateTo: '',
  });
  const [selectedInteraction, setSelectedInteraction] = useState<ChatInteraction | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Pagination par 10 éléments

  useEffect(() => {
    loadInteractions();
    loadStats();
  }, [filters, currentPage]);

  // Recharger périodiquement pour voir les nouvelles interactions
  useEffect(() => {
    const interval = setInterval(() => {
      loadInteractions();
      loadStats();
    }, 5000); // Recharger toutes les 5 secondes

    return () => clearInterval(interval);
  }, [filters, currentPage]);

  const loadInteractions = async () => {
    try {
      setLoading(true);
      const data = await chatHistoryApiService.getAllInteractions({
        search: filters.search || undefined,
        source: filters.source || undefined,
        dateFrom: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
        dateTo: filters.dateTo ? new Date(filters.dateTo) : undefined,
        page: currentPage,
      });
      
      console.log(`[AdminChatHistory] ${data.results.length} interactions chargées`);
      setInteractions(data.results);
      setTotalCount(data.count || 0);
    } catch (error) {
      console.error('Erreur lors du chargement des interactions:', error);
      alert('Erreur lors du chargement des interactions');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statistics = await chatHistoryApiService.getStatistics();
      setStats(statistics);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const handleViewDetails = (interaction: ChatInteraction) => {
    setSelectedInteraction(interaction);
    setShowModal(true);
  };

  const handleExport = async () => {
    try {
      const data = await chatHistoryApiService.getAllInteractions({});
      const json = JSON.stringify(data.results, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-history-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export');
    }
  };

  const handleClearOld = () => {
    alert('Cette fonctionnalité nécessite une implémentation backend supplémentaire. Contactez le développeur.');
  };

  const handleClearAll = () => {
    alert('Cette fonctionnalité nécessite une implémentation backend supplémentaire. Contactez le développeur.');
  };

  const getSourceBadgeColor = (source: ChatInteraction['source']) => {
    switch (source) {
      case 'api':
        return 'bg-blue-100 text-blue-700';
      case 'knowledge_base':
        return 'bg-green-100 text-green-700';
      case 'ai':
        return 'bg-purple-100 text-purple-700';
      case 'fallback':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getSourceLabel = (source: ChatInteraction['source']) => {
    switch (source) {
      case 'api':
        return 'API Islamique';
      case 'knowledge_base':
        return 'Base de Connaissances';
      case 'ai':
        return 'IA Générative';
      case 'fallback':
        return 'Fallback';
      default:
        return source;
    }
  };

  const paginatedInteractions = interactions; // Déjà paginé par l'API
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading) {
    return (
      <AdminLayout activeSection="/admin/chat-history">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-700 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Chargement...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="/admin/chat-history">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-3xl font-bold text-green-700 mb-2">Historique du Chat</h2>
              <p className="text-gray-600">Toutes les questions posées et leurs réponses</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleExport}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2"
              >
                <span>📥</span>
                <span>Exporter JSON</span>
              </button>
              <button
                onClick={handleClearOld}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2"
              >
                <span>🗑️</span>
                <span>Nettoyer anciennes</span>
              </button>
              <button
                onClick={handleClearAll}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2"
              >
                <span>⚠️</span>
                <span>Tout supprimer</span>
              </button>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4">
                  <div className="text-2xl font-bold">{stats.total_interactions || 0}</div>
                  <div className="text-sm opacity-90">Total interactions</div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-4">
                  <div className="text-2xl font-bold">{stats.total_users || 0}</div>
                  <div className="text-sm opacity-90">Utilisateurs uniques</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-4">
                  <div className="text-2xl font-bold">
                    {stats.average_response_time > 0
                      ? `${Math.round(stats.average_response_time)}ms`
                      : 'N/A'}
                  </div>
                  <div className="text-sm opacity-90">Temps réponse moyen</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl p-4">
                  <div className="text-2xl font-bold">{stats.by_source?.ai || 0}</div>
                  <div className="text-sm opacity-90">Réponses IA</div>
                </div>
              </div>

              {/* Stats détaillées */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Par source</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>API:</span>
                      <span className="font-bold">{stats.by_source?.api || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Base de connaissances:</span>
                      <span className="font-bold">{stats.by_source?.knowledge_base || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IA:</span>
                      <span className="font-bold">{stats.by_source?.ai || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Fallback:</span>
                      <span className="font-bold">{stats.by_source?.fallback || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Par modèle IA</h3>
                  <div className="space-y-1 text-sm">
                    {stats.by_ai_model && Object.entries(stats.by_ai_model).map(([model, count]: [string, any]) => (
                      <div key={model} className="flex justify-between">
                        <span className="capitalize">{model}:</span>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                    {(!stats.by_ai_model || Object.keys(stats.by_ai_model).length === 0) && (
                      <p className="text-gray-500 text-xs">Aucune réponse IA</p>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Questions fréquentes</h3>
                  <div className="space-y-1 text-sm max-h-32 overflow-y-auto">
                    {stats.frequent_questions && stats.frequent_questions.slice(0, 5).map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between">
                        <span className="truncate flex-1 mr-2" title={item.question}>
                          {item.question.substring(0, 30)}...
                        </span>
                        <span className="font-bold">{item.count}</span>
                      </div>
                    ))}
                    {(!stats.frequent_questions || stats.frequent_questions.length === 0) && (
                      <p className="text-gray-500 text-xs">Aucune donnée</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h3 className="text-xl font-bold text-gray-700 mb-4">Filtres</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🔍 Rechercher
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Question ou réponse..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📂 Source
              </label>
              <select
                value={filters.source}
                onChange={(e) =>
                  setFilters({ ...filters, source: e.target.value as ChatInteraction['source'] | '' })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Toutes les sources</option>
                <option value="api">API Islamique</option>
                <option value="knowledge_base">Base de Connaissances</option>
                <option value="ai">IA Générative</option>
                <option value="fallback">Fallback</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📅 Date de début
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📅 Date de fin
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Interactions List */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-700">
              Interactions ({totalCount})
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {paginatedInteractions.length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4">💬</div>
                <p className="text-gray-600 text-lg">Aucune interaction trouvée</p>
              </div>
            ) : (
              paginatedInteractions.map((interaction) => (
                <div
                  key={interaction.id}
                  className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleViewDetails(interaction)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getSourceBadgeColor(
                            interaction.source
                          )}`}
                        >
                          {getSourceLabel(interaction.source)}
                        </span>
                        {interaction.apiType && (
                          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                            {interaction.apiType}
                          </span>
                        )}
                        {interaction.aiModel && (
                          <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold">
                            {interaction.aiModel}
                          </span>
                        )}
                        {interaction.responseTime && (
                          <span className="text-xs text-gray-500">
                            {interaction.responseTime}ms
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {interaction.question}
                      </h3>
                      <p className="text-gray-600 mb-2 line-clamp-2">
                        {interaction.answer.substring(0, 200)}...
                      </p>
                      <p className="text-xs text-gray-500">
                        {interaction.timestamp.toLocaleString('fr-FR', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(interaction);
                      }}
                      className="ml-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Voir détails
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                loading={loading}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal Details */}
      {showModal && selectedInteraction && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-2xl font-bold text-gray-900">Détails de l'interaction</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Question</label>
                <div className="bg-gray-50 rounded-lg p-4">{selectedInteraction.question}</div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Réponse</label>
                <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap">
                  {selectedInteraction.answer}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Source</label>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${getSourceBadgeColor(
                        selectedInteraction.source
                      )}`}
                    >
                      {getSourceLabel(selectedInteraction.source)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Date et heure
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3">
                    {selectedInteraction.timestamp.toLocaleString('fr-FR', {
                      dateStyle: 'full',
                      timeStyle: 'long',
                    })}
                  </div>
                </div>
                {selectedInteraction.apiType && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Type d'API
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3">{selectedInteraction.apiType}</div>
                  </div>
                )}
                {selectedInteraction.aiModel && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Modèle IA
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3 capitalize">
                      {selectedInteraction.aiModel}
                    </div>
                  </div>
                )}
                {selectedInteraction.responseTime && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Temps de réponse
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3">
                      {selectedInteraction.responseTime}ms
                    </div>
                  </div>
                )}
                {selectedInteraction.userLocation && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Localisation
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3">
                      {selectedInteraction.userLocation.city} (
                      {selectedInteraction.userLocation.lat.toFixed(4)},{' '}
                      {selectedInteraction.userLocation.lng.toFixed(4)})
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

